import { useCallback, useEffect, useRef, useState } from 'react';

export interface WhopPayment {
  id: string;
  status: 'succeeded' | 'declined' | 'pending';
  requireAction: 'none' | 'redirect' | 'error';
  orderId: string;
  amount: number;
  currency: string;
  order?: {
    id: string;
    amount: number;
    currency: string;
  };
}

export interface UseWhopPaymentPollingOptions {
  apiService: {
    fetch: <T>(url: string, options: any) => Promise<T>;
  };
  checkoutSessionId: string;
  maxAttempts?: number;
  intervalMs?: number;
  onSuccess?: (payment: WhopPayment) => void;
  onMaxAttemptsReached?: () => void;
  onError?: (error: Error) => void;
}

export interface UseWhopPaymentPollingReturn {
  startPolling: (receiptId: string, orderId: string) => void;
  stopPolling: () => void;
  isPolling: boolean;
}

export const useWhopPaymentPolling = ({
  apiService,
  checkoutSessionId,
  maxAttempts = 60,
  intervalMs = 2000,
  onSuccess,
  onMaxAttemptsReached,
  onError,
}: UseWhopPaymentPollingOptions): UseWhopPaymentPollingReturn => {
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);
  const currentParamsRef = useRef<{ receiptId: string; orderId: string } | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsPolling(false);
    attemptsRef.current = 0;
    currentParamsRef.current = null;
  }, []);

  const startPolling = useCallback(
    (receiptId: string, orderId: string) => {
      stopPolling();

      attemptsRef.current = 0;
      currentParamsRef.current = { receiptId, orderId };
      setIsPolling(true);

      pollIntervalRef.current = setInterval(async () => {
        if (!currentParamsRef.current) return;

        try {
          attemptsRef.current++;
          console.log(`Whop payment polling attempt ${attemptsRef.current}/${maxAttempts}...`);

          const data = await apiService.fetch<WhopPayment>(
            `/api/v1/checkout-sessions/${checkoutSessionId}/whop-payment-status`,
            {
              method: 'POST',
              body: JSON.stringify(currentParamsRef.current),
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (data && (data.status === 'succeeded' || data.status === 'declined')) {
            stopPolling();
            onSuccess?.(data);
          } else if (attemptsRef.current >= maxAttempts) {
            stopPolling();
            onMaxAttemptsReached?.();
          }
        } catch (error) {
          console.warn(`Whop polling attempt ${attemptsRef.current} failed:`, error);
          if (attemptsRef.current >= maxAttempts) {
            stopPolling();
            onError?.(error instanceof Error ? error : new Error(String(error)));
          }
          // Otherwise keep polling through transient errors
        }
      }, intervalMs);
    },
    [apiService, checkoutSessionId, maxAttempts, intervalMs, onSuccess, onMaxAttemptsReached, onError, stopPolling],
  );

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
    isPolling,
  };
};
